import React from "react";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { MAIN_THEME_COLOR, SECONDARY_THEME_COLOR } from "../../../providers/theme/colors/colors";
import type { DeaneryOption, ParishOption } from "../../../utils/deanery_parishes";
import { eventOptions, type EventFormState, type RegistrationEventId } from "../data";

type CommonConfirmationProps = {
  open: boolean;
  isSubmitting: boolean;
  selectedDeanery: DeaneryOption | null;
  selectedParish: ParishOption | null;
  presidentName: string;
  presidentContactNumber: string;
  presidentEmail: string;
  animatorName: string;
  animatorContactNumber: string;
  onConfirm: () => void;
  onEdit: () => void;
};

type MainRegistrationConfirmationProps = CommonConfirmationProps & {
  type: "main";
  totalParticipantsCount: string;
  selectedEvents: RegistrationEventId[];
  eventForms: EventFormState;
  paymentPayerName?: string;
  paymentDate?: string;
  paymentAmount?: string;
  paymentScreenshot?: string;
};

type PitchBattleConfirmationProps = CommonConfirmationProps & {
  type: "pitch_battle";
  ideaTitle: string;
  entryType: "individual" | "group";
  teamLeadContact: string;
  participantCount: string;
  participantNames: string[];
};

type DycConfirmationModalProps =
  | MainRegistrationConfirmationProps
  | PitchBattleConfirmationProps;

export default function DycConfirmationModal(props: DycConfirmationModalProps) {
  const {
    open,
    isSubmitting,
    selectedDeanery,
    selectedParish,
    presidentName,
    presidentContactNumber,
    presidentEmail,
    animatorName,
    animatorContactNumber,
    onConfirm,
    onEdit,
  } = props;

  const formattedPaymentDate =
    props.type === "main" && props.paymentDate
      ? props.paymentDate.includes("-")
        ? props.paymentDate.split("-").reverse().join("/")
        : props.paymentDate
      : "N/A";

  return (
    <Dialog
      open={open}
      onClose={onEdit}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          p: { xs: 1, sm: 2 },
        },
      }}
    >
      <DialogTitle sx={{ pt: 2, pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "50%",
              backgroundColor: `${MAIN_THEME_COLOR}15`,
              color: MAIN_THEME_COLOR,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CheckCircleOutlineRoundedIcon sx={{ fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={800} color={MAIN_THEME_COLOR}>
              Confirm Registration Details
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Please review your details carefully before final submission.
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ py: 2 }}>
        <Stack spacing={2.5}>
          {/* Unit / Parish Information */}
          <Paper
            variant="outlined"
            sx={{ p: 2, borderRadius: 3, backgroundColor: "#fafafa" }}
          >
            <Typography variant="subtitle2" fontWeight={800} color={MAIN_THEME_COLOR} mb={1}>
              Unit & Parish Details
            </Typography>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Deanery
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {selectedDeanery?.name || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Parish / Unit
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {selectedParish?.value || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  President Name & Contact
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {presidentName} ({presidentContactNumber})
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Animator Name & Contact
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {animatorName} ({animatorContactNumber})
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  President Email
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {presidentEmail}
                </Typography>
              </Grid>
              {props.type === "main" ? (
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Total Unit Participants (All Members)
                  </Typography>
                  <Typography variant="body2" fontWeight={700} color={SECONDARY_THEME_COLOR}>
                    {props.totalParticipantsCount}
                  </Typography>
                </Grid>
              ) : null}
            </Grid>
          </Paper>

          {/* Payment Details Preview for Main Registration */}
          {props.type === "main" ? (
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, backgroundColor: "#fffcf7" }}>
              <Typography variant="subtitle2" fontWeight={800} color={MAIN_THEME_COLOR} mb={1}>
                Online Payment Details
              </Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Payer Name
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {props.paymentPayerName || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Payment Date (DD/MM/YYYY)
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {formattedPaymentDate}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Amount Paid
                  </Typography>
                  <Typography variant="body2" fontWeight={700} color="success.main">
                    ₹{props.paymentAmount || "0"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Payment Screenshot
                  </Typography>
                  {props.paymentScreenshot ? (
                    <Box sx={{ mt: 0.5 }}>
                      <img
                        src={props.paymentScreenshot}
                        alt="Payment Screenshot"
                        style={{ maxHeight: 60, borderRadius: 6, border: "1px solid #ccc" }}
                      />
                    </Box>
                  ) : (
                    <Typography variant="body2" color="error">
                      Not uploaded
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </Paper>
          ) : null}

          {/* Form Specific Details */}
          {props.type === "main" ? (
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
              <Typography variant="subtitle2" fontWeight={800} color={MAIN_THEME_COLOR} mb={1}>
                Selected Participation Events ({props.selectedEvents.length})
              </Typography>

              {props.selectedEvents.length === 0 ? (
                <Typography variant="body2" color="warning.main" fontStyle="italic">
                  No events selected (Unit Registration Only).
                </Typography>
              ) : (
                <Stack spacing={2} mt={1}>
                  {props.selectedEvents.map((eventId) => {
                    const label = eventOptions.find((e) => e.id === eventId)?.label || eventId;

                    if (eventId === "spotlight") {
                      const count = Number(props.eventForms.spotlight.participantCount || "1");
                      const names = props.eventForms.spotlight.participantNames
                        .slice(0, count)
                        .join(", ");
                      return (
                        <Box key={eventId} sx={{ p: 1.5, borderLeft: `3px solid ${MAIN_THEME_COLOR}`, bg: "#fcfcfc" }}>
                          <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
                            <Typography variant="body2" fontWeight={700}>
                              {label}
                            </Typography>
                            <Chip label={`${count} Participants`} size="small" color="primary" variant="outlined" />
                          </Stack>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Performance Slot: <strong>{props.eventForms.spotlight.performingEventName}</strong>
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Team Lead Contact: <strong>{props.eventForms.spotlight.teamLeadContact}</strong>
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Participants: <strong>{names}</strong>
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Description: {props.eventForms.spotlight.eventDescription}
                          </Typography>
                        </Box>
                      );
                    }

                    if (eventId === "jam") {
                      return (
                        <Box key={eventId} sx={{ p: 1.5, borderLeft: `3px solid ${MAIN_THEME_COLOR}`, bg: "#fcfcfc" }}>
                          <Typography variant="body2" fontWeight={700} mb={0.5}>
                            {label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Participant: <strong>{props.eventForms.jam.participantName}</strong> ({props.eventForms.jam.participantContact})
                          </Typography>
                        </Box>
                      );
                    }

                    if (eventId === "ecomosaic") {
                      const eco = props.eventForms.ecomosaic;
                      return (
                        <Box key={eventId} sx={{ p: 1.5, borderLeft: `3px solid ${MAIN_THEME_COLOR}`, bg: "#fcfcfc" }}>
                          <Typography variant="body2" fontWeight={700} mb={0.5}>
                            {label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Participant 1: <strong>{eco.primaryName}</strong> ({eco.primaryContact})
                          </Typography>
                          {eco.hasSecondParticipant && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              Participant 2: <strong>{eco.secondaryName}</strong> ({eco.secondaryContact})
                            </Typography>
                          )}
                        </Box>
                      );
                    }

                    if (eventId === "faithvault") {
                      const fv = props.eventForms.faithvault;
                      return (
                        <Box key={eventId} sx={{ p: 1.5, borderLeft: `3px solid ${MAIN_THEME_COLOR}`, bg: "#fcfcfc" }}>
                          <Typography variant="body2" fontWeight={700} mb={0.5}>
                            {label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Team Lead Contact: <strong>{fv.teamLeadContact}</strong>
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Members: <strong>{fv.memberNames.filter(Boolean).join(", ")}</strong>
                          </Typography>
                        </Box>
                      );
                    }

                    if (eventId === "pitchbattle") {
                      return (
                        <Box key={eventId} sx={{ p: 1.5, borderLeft: `3px solid ${MAIN_THEME_COLOR}`, bg: "#fcfcfc" }}>
                          <Typography variant="body2" fontWeight={700} mb={0.5}>
                            {label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Participation Confirmed (Submissions registered via Pitch Battle Form)
                          </Typography>
                        </Box>
                      );
                    }

                    if (eventId === "rhythmroots") {
                      const rr = props.eventForms.rhythmroots;
                      const count = Number(rr.participantCount || "0");
                      const names = rr.participantNames.slice(0, count).filter(Boolean);
                      return (
                        <Box key={eventId} sx={{ p: 1.5, borderLeft: `3px solid ${MAIN_THEME_COLOR}`, bg: "#fcfcfc" }}>
                          <Typography variant="body2" fontWeight={700} mb={0.5}>
                            {label} (Compulsory)
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Participants ({count}): <strong>{names.join(", ")}</strong>
                          </Typography>
                        </Box>
                      );
                    }

                    return null;
                  })}
                </Stack>
              )}
            </Paper>
          ) : (
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
              <Typography variant="subtitle2" fontWeight={800} color={MAIN_THEME_COLOR} mb={1}>
                Pitch Battle Details
              </Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Pitch Title
                  </Typography>
                  <Typography variant="body2" fontWeight={700}>
                    {props.ideaTitle}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Entry Type & Count
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {props.entryType === "individual" ? "Individual Entry" : "Group Entry"} ({props.participantCount} Participant{Number(props.participantCount) > 1 ? "s" : ""})
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Team Lead Contact
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {props.teamLeadContact}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Participant Name(s)
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {props.participantNames.slice(0, Number(props.participantCount)).filter(Boolean).join(", ")}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, justifyContent: "space-between" }}>
        <Button
          onClick={onEdit}
          disabled={isSubmitting}
          variant="outlined"
          sx={{ borderRadius: 999, px: 3 }}
        >
          Edit Details
        </Button>
        <Button
          onClick={onConfirm}
          disabled={isSubmitting}
          variant="contained"
          sx={{
            borderRadius: 999,
            px: 4,
            background: `linear-gradient(90deg, ${MAIN_THEME_COLOR} 0%, ${SECONDARY_THEME_COLOR} 100%)`,
          }}
        >
          {isSubmitting ? "Submitting..." : "Confirm & Submit"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
